import { supabase } from "@/lib/supabase";

export interface InsuranceExcessSettings {
  currentTarget: number;
  step6mo: number;
  step12mo: number;
  step18moFull: number;
  weeklyContribution: number;
  noClaimBonusAmount: number;
  tenureWindowMonths: number;
  minMonthsBeforeClaim: number;
}

export const DEFAULT_INSURANCE_EXCESS_SETTINGS: InsuranceExcessSettings = {
  currentTarget: 2500,
  step6mo: 800,
  step12mo: 1600,
  step18moFull: 2500,
  weeklyContribution: 15,
  noClaimBonusAmount: 300,
  tenureWindowMonths: 18,
  minMonthsBeforeClaim: 6,
};

// weeklyContribution deliberately has no row of its own -- it always mirrors
// Payment Settings' "Insurance Excess" (`insurance_excess`), the number
// actually deducted from every driver's pay each week. Keeping two
// independently-editable "$/week" numbers meant they could silently drift
// apart; now there's exactly one place to change it and everything
// (the fund ledger, this settings screen, and the repayment-plan default)
// agrees with what payroll actually takes.
const SETTING_NAME_BY_KEY: Record<Exclude<keyof InsuranceExcessSettings, "weeklyContribution">, string> = {
  currentTarget: "insurance_excess_current_target",
  step6mo: "insurance_excess_step_6mo",
  step12mo: "insurance_excess_step_12mo",
  step18moFull: "insurance_excess_step_18mo_full",
  noClaimBonusAmount: "insurance_excess_no_claim_bonus_amount",
  tenureWindowMonths: "insurance_excess_tenure_window_months",
  minMonthsBeforeClaim: "insurance_excess_min_months_before_claim",
};

export async function loadInsuranceExcessSettings(): Promise<InsuranceExcessSettings> {
  const { data, error } = await supabase
    .from("settings")
    .select("name, value")
    .in("name", [...Object.values(SETTING_NAME_BY_KEY), "insurance_excess"]);

  if (error || !data) return DEFAULT_INSURANCE_EXCESS_SETTINGS;

  const byName = new Map(data.map((row) => [row.name, row.value]));
  const settings = { ...DEFAULT_INSURANCE_EXCESS_SETTINGS };
  (Object.keys(SETTING_NAME_BY_KEY) as (keyof typeof SETTING_NAME_BY_KEY)[]).forEach((key) => {
    const raw = byName.get(SETTING_NAME_BY_KEY[key]);
    const n = raw != null ? parseFloat(String(raw)) : NaN;
    if (!Number.isNaN(n) && n >= 0) settings[key] = n;
  });

  const weekly = parseFloat(String(byName.get("insurance_excess") ?? ""));
  if (!Number.isNaN(weekly) && weekly >= 0) settings.weeklyContribution = weekly;

  return settings;
}

export interface FundLedgerEntry {
  entry_type: "contribution" | "charge" | "refund" | "adjustment";
  amount: number;
  running_balance: number;
  created_at: string;
}

export async function loadDriverFundLedger(driverName: string): Promise<FundLedgerEntry[]> {
  const { data, error } = await supabase
    .from("driver_excess_fund_ledger")
    .select("entry_type, amount, running_balance, created_at")
    .eq("driver_name", driverName)
    .order("created_at", { ascending: true });

  if (error) {
    console.error("Error loading driver excess fund ledger:", error);
    return [];
  }
  return data || [];
}

export interface FundStatus {
  monthsContributing: number | null;
  currentBalance: number;
  claimableTier: number;
  eligibleForClaim: boolean;
  /** Eligible for the No Claim Rebate (18 claim-free months since the later of the start date, last claim and last rebate paid). */
  eligibleForBonus: boolean;
  /** When the next No Claim Rebate falls due (ISO date), or null if there's no start date yet. */
  rebateDueOn: string | null;
}

interface FundDriver {
  insurance_start_date: string | null;
  insurance_last_claim_date: string | null;
  /** The date the last No Claim Rebate was paid -- the next one is due a full tenure window after it. */
  lastRebatePaidOn?: string | null;
}

const isoDay = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

/** `iso` plus whole calendar months (the day of month is kept where it exists, else the month's last day). */
export function addMonthsIso(iso: string, months: number): string {
  const [y, m, d] = iso.slice(0, 10).split("-").map(Number);
  const target = new Date(y, m - 1 + months, 1);
  const lastDay = new Date(target.getFullYear(), target.getMonth() + 1, 0).getDate();
  target.setDate(Math.min(d, lastDay));
  return isoDay(target);
}

/** Whole calendar months from `fromIso` to `now`. */
function monthsBetween(fromIso: string, now: Date): number {
  const start = new Date(fromIso);
  let months = (now.getFullYear() - start.getFullYear()) * 12 + (now.getMonth() - start.getMonth());
  if (now.getDate() < start.getDate()) months -= 1;
  return Math.max(0, months);
}

/**
 * Implements the graduated tiers from the signed Insurance Excess Fund
 * agreement: nothing claimable under minMonthsBeforeClaim, step6mo once
 * past it, step18moFull once past tenureWindowMonths. The 12-month
 * midpoint tier isn't its own setting (only its dollar value, step12mo,
 * is) -- its timing is derived as the midpoint between the two configured
 * month thresholds so it stays proportional if either is changed.
 */
export function calculateFundStatus(
  driver: FundDriver,
  ledgerEntries: FundLedgerEntry[],
  settings: InsuranceExcessSettings,
  now: Date = new Date()
): FundStatus {
  // The tenure clock resets on the later of insurance_start_date and
  // insurance_last_claim_date -- a claim restarts the graduated cycle per
  // the agreement ("When a claim has been made the contributor reverts to
  // the start of these conditions again").
  const clockStart =
    driver.insurance_last_claim_date &&
    (!driver.insurance_start_date || new Date(driver.insurance_last_claim_date) > new Date(driver.insurance_start_date))
      ? driver.insurance_last_claim_date
      : driver.insurance_start_date;

  let monthsContributing: number | null = null;
  if (clockStart) monthsContributing = monthsBetween(clockStart, now);

  const currentBalance = ledgerEntries.length > 0 ? ledgerEntries[ledgerEntries.length - 1].running_balance : 0;

  const midpointMonths = (settings.minMonthsBeforeClaim + settings.tenureWindowMonths) / 2;

  let claimableTier = 0;
  const eligibleForClaim = monthsContributing != null && monthsContributing >= settings.minMonthsBeforeClaim;
  if (eligibleForClaim && monthsContributing != null) {
    if (monthsContributing >= settings.tenureWindowMonths) claimableTier = settings.step18moFull;
    else if (monthsContributing >= midpointMonths) claimableTier = settings.step12mo;
    else claimableTier = settings.step6mo;
  }

  // The No Claim Rebate: 18 claim-free months from the start date / last claim, and again 18 months
  // after each time it is paid. A claim restarts the cycle (clockStart moves past the last rebate).
  const rebateClockStart =
    clockStart && driver.lastRebatePaidOn && new Date(driver.lastRebatePaidOn) > new Date(clockStart)
      ? driver.lastRebatePaidOn
      : clockStart;
  const eligibleForBonus = !!rebateClockStart && monthsBetween(rebateClockStart, now) >= settings.tenureWindowMonths;
  const rebateDueOn = rebateClockStart ? addMonthsIso(rebateClockStart, settings.tenureWindowMonths) : null;

  return { monthsContributing, currentBalance, claimableTier, eligibleForClaim, eligibleForBonus, rebateDueOn };
}

// ---------------------------------------------------------------------------
// No Claim Rebate payments and the worked-weeks backfill
// ---------------------------------------------------------------------------

export interface NoClaimRebate {
  id: string;
  driver_name: string;
  paid_on: string;
  amount: number;
  note: string | null;
}

export async function loadNoClaimRebates(): Promise<NoClaimRebate[]> {
  const { data, error } = await supabase
    .from("insurance_no_claim_rebates")
    .select("id, driver_name, paid_on, amount, note")
    .order("paid_on", { ascending: true });
  if (error) {
    console.error("Error loading No Claim Rebates:", error);
    return [];
  }
  return ((data as any[]) || []).map((r) => ({ ...r, amount: Number(r.amount) }));
}

export async function recordNoClaimRebate(args: { driverName: string; paidOn: string; amount: number; note?: string }): Promise<void> {
  const { error } = await supabase.rpc("record_no_claim_rebate", {
    p_driver_name: args.driverName,
    p_paid_on: args.paidOn,
    p_amount: args.amount,
    p_note: args.note ?? null,
  });
  if (error) throw error;
}

export async function deleteNoClaimRebate(id: string): Promise<void> {
  const { error } = await supabase.rpc("delete_no_claim_rebate", { p_id: id });
  if (error) throw error;
}

export interface ExcessBackfillRow {
  driver_name: string;
  weeks: number;
  amount: number;
  first_week: string;
  last_week: string;
}

export interface ExcessBackfillResult {
  applied: boolean;
  through: string | null;
  weekly: number;
  total_weeks: number;
  drivers: ExcessBackfillRow[];
}

/** Preview (apply = false) or add the weekly contributions for every worked week since each driver's start date. */
export async function backfillExcessFund(apply: boolean): Promise<ExcessBackfillResult> {
  const { data, error } = await supabase.rpc("backfill_excess_fund_contributions", { p_apply: apply });
  if (error) throw error;
  return data as ExcessBackfillResult;
}
